import { FetchPolicy } from "@apollo/client";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles
} from "@material-ui/core";
import {
  ConsultantPresentationType,
  GenerateConsultantPresentationMutation,
  useGenerateConsultantPresentationMutation,
  useGetConsultantCreatePresentationDataLazyQuery,
  useSendConsultantPresentationEmailMutation,
  GeneratePresentationAttachmentError,
  ConsultantReferenceType
} from "@bonliva-crm/graphql";
import { Spinner } from "@common";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLocale, useSnackbars } from "@hooks";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  firstFormSchema,
  FirstFormValues,
  getInitialValue,
  secondFormSchema,
  SecondFormValues,
  thirdFormSchema,
  ThirdFormValues
} from "./utils/form-values";
import { createStepData, Steps } from "./utils/step-data";

type Props = {
  open: boolean;
  consultantId: string;
  fetchPolicy?: FetchPolicy;
  onClose: () => void;
};

export function CreatePresentationModal(props: Props) {
  const classes = useStyles();
  const snack = useSnackbars();
  const { locale } = useLocale();
  const [currentStep, setCurrentStep] = useState(Steps.First);
  const [presentationData, setPresentationData] = useState<
    GenerateConsultantPresentationMutation["consultantGeneratePresentation"] | null
  >(null);
  const [attachmentErrors, setAttachmentErrors] = useState<
    GeneratePresentationAttachmentError[]
  >([]);

  const [loadConsultantData, consultantDataQuery] =
    useGetConsultantCreatePresentationDataLazyQuery({
      variables: {
        consultantId: props.consultantId
      },
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        firstForm.reset(getInitialValue(data));
      }
    });

  const [
    generateConsultantPresentationMutation,
    { loading: generatePresentationLoading }
  ] = useGenerateConsultantPresentationMutation({
    onError: (e) => {
      console.error(e);
      snack.showError(locale.ErrorOccurred);
    }
  });

  useEffect(() => {
    if (props.open) {
      loadConsultantData();
    }
  }, [props.open, loadConsultantData]);

  const generateConsultantPresentation = (data: FirstFormValues) => {
    setAttachmentErrors([]);

    generateConsultantPresentationMutation({
      variables: {
        consultantId: props.consultantId,
        referenceIds: [
          ...data.selectedReferenceDocuments.filter(
            (x) => x.type === ConsultantReferenceType.Reference
          )
        ].map((x) => x.referenceId),
        evaluatesIds: [
          ...[
            ...data.selectedReferenceDocuments.filter(
              (x) => x.type === ConsultantReferenceType.Evaluation
            )
          ].map((x) => x.referenceId)
        ],
        evaluatesThreeMonthsIds: [
          ...data.selectedReferenceDocuments.filter(
            (x) => x.type === ConsultantReferenceType.UUCrossQuestions
          )
        ].map((x) => x.referenceId),
        fileIds: data.selectedFiles,
        presentationCountry: data.presentationCountry,
        presentationType: data.presentationType,
        contactPerson: data.contactPerson._id,
        generateCV: data.generateCV === "true",
        presentationText:
          data.presentationType === ConsultantPresentationType.NoText
            ? ""
            : data.presentationText
      },
      onCompleted(data) {
        if (data.consultantGeneratePresentation.attachmentErrors.length > 0) {
          setAttachmentErrors(data.consultantGeneratePresentation.attachmentErrors);
        } else {
          setPresentationData(data.consultantGeneratePresentation);
          setCurrentStep(Steps.Second);
        }
      }
    });
  };

  const [sendConsultantPresentationEmailMutation, { loading: sendEmailLoading }] =
    useSendConsultantPresentationEmailMutation({
      onError: (e) => {
        console.error(e);
        snack.showError(locale.ErrorOccurred);
      }
    });

  const sendConsultantPresentationEmail = (
    data: ThirdFormValues,
    onCompleted: () => void
  ) => {
    if (!presentationData?.presentation._id) {
      return;
    }

    sendConsultantPresentationEmailMutation({
      variables: {
        consultantId: props.consultantId,
        presentationId: presentationData.presentation._id,
        recipients: data.recipients,
        subject: data.subject,
        body: data.body,
        attachments: data.attachments.map((attachment) => attachment.NodeID)
      },
      onCompleted() {
        props.onClose && props.onClose();
        snack.showSuccess(locale.Sent);
        onCompleted();
      }
    });
  };

  const firstForm = useForm<FirstFormValues>({
    defaultValues: getInitialValue(consultantDataQuery.data),
    resolver: yupResolver(firstFormSchema)
  });

  const selectedFiles = firstForm.getValues("selectedFiles");

  useEffect(() => {
    if (attachmentErrors.length > 0) {
      const actualAttachmentErrors = attachmentErrors.filter((attachment) =>
        selectedFiles.includes(attachment.fileId)
      );
      setAttachmentErrors(actualAttachmentErrors);
    }
  }, [selectedFiles]);

  const secondForm = useForm<SecondFormValues>({
    defaultValues: {
      presentationVerified: false
    },
    resolver: yupResolver(secondFormSchema)
  });

  const thirdForm = useForm<ThirdFormValues>({
    defaultValues: {
      recipients: [],
      subject: locale.presentation,
      body: "",
      attachments: []
    },
    resolver: yupResolver(thirdFormSchema)
  });

  const resetPresentationState = () => {
    firstForm.reset();
    secondForm.reset();
    thirdForm.reset();
    setCurrentStep(Steps.First);
    setAttachmentErrors([]);
  };

  const stepData = createStepData({
    consultantId: props.consultantId,
    setCurrentStep,
    onClose: () => {
      props.onClose();
      resetPresentationState();
    },
    stepInput: {
      first: {
        form: firstForm,
        attachmentErrors,
        onSubmit: firstForm.handleSubmit((data) => {
          generateConsultantPresentation(data);
        })
      },
      second: {
        form: secondForm,
        presentationUrl: presentationData?.presentation.ViewURL
      },
      third: {
        form: thirdForm,
        onSubmit: thirdForm.handleSubmit((data) => {
          sendConsultantPresentationEmail(data, resetPresentationState);
        })
      }
    }
  });

  let content = null;

  if (generatePresentationLoading || sendEmailLoading) {
    content = <Spinner fullHeight={true} />;
  } else {
    content = stepData[currentStep].component;
  }

  return (
    <Dialog
      open={props.open}
      onClose={() => {
        props.onClose();
        resetPresentationState();
      }}
      maxWidth="lg"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>{locale.sendPresentation}</DialogTitle>

      <DialogContent style={{ display: "flex", flexDirection: "column" }}>
        {content}
      </DialogContent>

      <DialogActions>
        {stepData[currentStep].leftCta}
        {stepData[currentStep].rightCta}
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles({
  dialogPaper: {
    minHeight: "80vh"
  }
});
